/**
 * PostWriterPage
 *
 * Main content creation page with AI-powered topic management.
 * Includes submenu for navigation and topics management section.
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopicsList } from '../components/topics/TopicsList';
import { TopicGenerationModal } from '../components/topics/TopicGenerationModal';
import { PostWriterSubmenuMemo } from '../components/post-writer/PostWriterSubmenu';
import { useTopics } from '../hooks/useTopics';
import type { Id } from '../../convex/_generated/dataModel';

type PostWriterTab = 'topics' | 'drafts' | 'scheduled';

export const PostWriterPage = () => {
  const navigate = useNavigate();
  const [activeTab, _setActiveTab] = useState<PostWriterTab>('topics');
  const [isGenerationModalOpen, setIsGenerationModalOpen] = useState(false);

  const { topics, isLoading, deleteTopic } = useTopics();

  // Listen for custom event to open modal (from empty state button)
  useEffect(() => {
    const handleOpenModal = () => {
      setIsGenerationModalOpen(true);
    };

    window.addEventListener('openTopicGenerationModal', handleOpenModal);

    return () => {
      window.removeEventListener('openTopicGenerationModal', handleOpenModal);
    };
  }, []);

  const handleOpenGenerationModal = useCallback(() => {
    setIsGenerationModalOpen(true);
  }, []);

  const handleCloseGenerationModal = useCallback(() => {
    setIsGenerationModalOpen(false);
  }, []);

  const handleGenerationSuccess = useCallback(() => {
    // Topics list will refresh automatically via Convex reactivity
    setIsGenerationModalOpen(false);
  }, []);

  const handleGeneratePost = useCallback(
    (topicId: Id<'topics'>) => {
      // Navigate to multi-AI post generation page with topic ID
      navigate(`/post-generation/${topicId}`);
    },
    [navigate]
  );

  return {
    submenu: (
      <PostWriterSubmenuMemo
        onGenerateTopics={handleOpenGenerationModal}
        showGenerateButton={activeTab === 'topics'}
      />
    ),
    mainContent: (
      <div className="min-h-full bg-background-secondary">
        <div className="py-8 px-10">
          {/* Topics Tab */}
          {activeTab === 'topics' && (
            <TopicsList
              topics={topics || []}
              onDelete={deleteTopic}
              onGenerate={handleGeneratePost}
              isLoading={isLoading}
            />
          )}

          {/* Drafts Tab (Placeholder) */}
          {activeTab === 'drafts' && (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Drafts
                </h2>
                <p className="text-gray-600 dark:text-gray-400">Coming soon...</p>
              </div>
            </div>
          )}

          {/* Scheduled Tab (Placeholder) */}
          {activeTab === 'scheduled' && (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Scheduled Posts
                </h2>
                <p className="text-gray-600 dark:text-gray-400">Coming soon...</p>
              </div>
            </div>
          )}
        </div>

        {/* Topic Generation Modal */}
        <TopicGenerationModal
          isOpen={isGenerationModalOpen}
          onClose={handleCloseGenerationModal}
          onSuccess={handleGenerationSuccess}
        />
      </div>
    ),
  };
};
